// launch-decision.ts — Dice's money mode is a property of the LAUNCH, carried in the URL fragment (SC-61814; the same
// rule Plinko ships as SC-61834).
//
// It used to be inferred from `blocklabs.rgs.real`, a localStorage marker every game on the origin shares, which the SDK
// writes on every REAL ticket exchange (and Dice cleared on a dead session). So a ticketless open after REAL resumed
// REAL, a load after a dead session booted the FUN demo with a fake balance, and another game's REAL session leaked
// into a bare Dice load. The rule now (pure — unit-tested in launch-decision.test.ts; launch.ts applies it to the DOM):
//   #t=<ticket>     REAL fresh launch (the operator's single-use ticket)
//   #mode=pending   written by launch.ts in place of the ticket until the session is ready: a reload of an
//                   interrupted fresh launch runs NEITHER mode, on any build (never the demo, never an older session)
//   #mode=real      REAL resume: written by launch.ts once a REAL session is ready, so a reload stays REAL
//   #mode=fun       the in-browser demo, even when a REAL session exists in storage
//   anything else is a bare load, decided by the build's policy — and never runs REAL.

export type BareLoadPolicy = "fun" | "launch-required";

export type LaunchDecision =
  | { readonly kind: "real-fresh"; readonly ticket: string }
  | { readonly kind: "real-resume" }
  | { readonly kind: "fun" }
  | { readonly kind: "launch-required" };

export const REAL_PENDING_FRAGMENT = "#mode=pending";
export const REAL_RESUME_FRAGMENT = "#mode=real";

// The build's bare-load policy, from VITE_BARE_LOAD_POLICY (validated at build time in vite.config.ts). Every Dice build
// is served from the root, so the asset base cannot tell a demo host from a REAL-capable one: the policy is its own
// setting. Default "fun": the deployed standalone hosts (originals-dice.<env>, no /v1) are FUN-only and keep booting
// the demo on a bare URL. A REAL-capable deployment sets "launch-required": there a bare load is not a launch (an old
// REAL page reloaded after a deploy, a bookmark, a stray open), so it runs neither mode and asks for a casino launch.
export function bareLoadPolicy(configured: string | undefined): BareLoadPolicy {
  return configured === "launch-required" ? "launch-required" : "fun";
}

// Once a fresh REAL session is ready, may the fragment become #mode=real? Only while it still stands for this launch
// (#mode=pending, exactly as launch.ts left it): any other value is a newer navigation — a new #t=, #mode=fun — whose
// queued hashchange must reload; overwriting it would swallow that launch.
export function mayMarkReady(currentFragment: string, canonicalFragment: string): boolean {
  return currentFragment === canonicalFragment && currentFragment === REAL_PENDING_FRAGMENT;
}

export function decideLaunch(fragment: string, bareLoad: BareLoadPolicy): LaunchDecision {
  const params = parseFragment(fragment);
  const bare: LaunchDecision = { kind: bareLoad };
  if (params === null) return bare; // malformed encoding: not a launch
  const tickets = params.filter(([k]) => k === "t");
  const modes = params.filter(([k]) => k === "mode");
  if (tickets.length === 1 && modes.length === 0) {
    const ticket = tickets[0]?.[1] ?? "";
    return ticket !== "" ? { kind: "real-fresh", ticket } : bare;
  }
  if (tickets.length === 0 && modes.length === 1) {
    const mode = modes[0]?.[1];
    if (mode === "real") return { kind: "real-resume" };
    if (mode === "fun") return { kind: "fun" };
    if (mode === "pending") return { kind: "launch-required" };
  }
  return bare; // none, duplicated, conflicting (t + mode) or unknown selector
}

// `k=v&k2=v2` (an optional leading '#'), percent-decoded. A '+' stays a '+' (unlike URLSearchParams) so a ticket
// is never altered. null when any part is malformed.
function parseFragment(fragment: string): Array<[string, string]> | null {
  const body = fragment.startsWith("#") ? fragment.slice(1) : fragment;
  if (body === "") return [];
  const out: Array<[string, string]> = [];
  for (const part of body.split("&")) {
    if (part === "") continue;
    const eq = part.indexOf("=");
    const rawKey = eq < 0 ? part : part.slice(0, eq);
    const rawValue = eq < 0 ? "" : part.slice(eq + 1);
    try {
      out.push([decodeURIComponent(rawKey), decodeURIComponent(rawValue)]);
    } catch {
      return null;
    }
  }
  return out;
}
